'use client';
import CategoryGrid, { GridType } from '@components/shared/CategoryGrid';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { useState, memo, useRef, useEffect } from 'react';
import GridItem from '../shared/StoriesGridItem';
import useUpdateEffect from '@hooks/useUpdateEffect';
import Section from '@repo/ui/src/components/Section';
import { Data } from '@typings/productGrid';
import searchStories from '@api/search/searchStoriesFn';
import { classNames } from '@repo/ui/src/utils/classNames';

interface Category {
  name: string;
  value: string;
}

interface SortOption {
  value: string;
  label: string;
}

interface StoriesGridProps {
  title?: string;
  filters?: {
    categories?: Category[];
  };
  sortOptions?: SortOption[];
  _data?: Data;
}

const StoriesGrid = ({ title, filters, sortOptions, _data }: StoriesGridProps) => {
  const { locale } = useCurrentUrl();
  const [data, setData] = useState(_data);
  const [currentTab, setCurrentTab] = useState(0);
  const [currentCategory, setCurrentCategory] = useState(
    filters?.categories?.length ? filters?.categories[0].value : null,
  );
  const [loading, setLoading] = useState(false);
  const tabsRef = useRef<(HTMLButtonElement | null)[]>([]);
  const indicatorRef = useRef<HTMLDivElement>(null);

  const getSortParam = (sort?: SortOption): { field: string; direction: string } | null => {
    if (!sort?.value) return null;
    const [field, direction] = sort.value.split(',');
    return { field, direction };
  };

  const loadData = async (
    { page }: { page: number },
  ): Promise<any> => {
    return await searchStories({
      filters: currentCategory ? JSON.stringify({ categories: currentCategory }) : undefined,
      page: page.toString(),
      pageSize: '10',
      sort: JSON.stringify(getSortParam(sortOptions?.[0])),
      lang: locale,
    });
  };

  const selectTab = (categoryValue: string, index: number): void => {
    setCurrentTab(index);
    setCurrentCategory(categoryValue);
  };

  useEffect(() => {
    if (indicatorRef.current && tabsRef.current[currentTab]) {
      const activeTab = tabsRef.current[currentTab];
      if (activeTab) {
        const { offsetLeft, offsetWidth } = activeTab;
        indicatorRef.current.style.left = `${offsetLeft}px`;
        indicatorRef.current.style.width = `${offsetWidth}px`;
      }
    }
  }, [currentTab]);

  useUpdateEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const res = await loadData({ page: 0 });
      setData(res);
      setLoading(false);
    };  
    fetchData();
  }, [currentCategory]);

  return (
    <Section gutterX basePadding white>
      {title && (
        <h2 className="tw-text-center tra-text-web-heading-2">{title}</h2>
      )}
      {filters?.categories && (
        <div className="tw-flex tw-justify-center tw-py-2 md:tw-py-4">
          <div className="tw-overflow-x-auto tw-flex tw-py-5 tw-px-3 tw-scrollbar-hide tw-relative">
            {filters?.categories.map((category, i) => (
              <button
                key={`${category.value}_${i}`}
                className={classNames(
                  'tw-mx-3 tra-text-web-heading-5',
                  currentTab === i ? 'tw-text-primary-600' : 'tw-text-surface-450'
                )}
                onClick={() => selectTab(category.value, i)}
                ref={(el) => {
                  tabsRef.current[i] = el;
                }}
              >
                {category.name}
              </button>
            ))}
            <div 
              ref={indicatorRef} 
              className="tw-absolute tw-bottom-3 tw-h-1 tw-bg-action-300 tw-transition-all tw-duration-300 tw-ease-in-out"
              aria-hidden="true"
            />
          </div>
        </div>
      )}
      {loading && (
        <div className="tw-flex tw-justify-center tw-relative">
          <div className="tw-absolute tw-top-[50px] tw-z-10 tw-h-[100px] tw-w-[100px] tw-border-8 tw-border-action-400 tw-border-t-transparent tw-rounded-full tw-animate-spin"></div>
        </div>
      )}
      <CategoryGrid
        _data={data}
        loadData={loadData}
        GridItemComponent={GridItem}
        type={GridType.Story}
        darkBackground
        disableBorder
        content={{}} 
        hideAddToCartButton={false}
      />
    </Section>
  );
};

export default memo(StoriesGrid);